/**
 * ActivationDialogHost — the class-D dialog host shared by every activation surface
 * (contracts §ACTION_ROUTING class D: "the checklist mounts the real dialog with a
 * preset — zero new UI"). Follow-up F1.
 *
 * Why a hook + a node instead of per-surface state: the checklist is rendered on the
 * Launch page AND in the sidebar (short form + nav badge). A class-D action must open
 * its dialog IN PLACE on whichever surface the user clicked from — the sidebar must not
 * navigate away from the page the user is on. `useActivationDialogHost` owns the open
 * state, returns the `openDialog` callback to hand to `ActivationChecklist`, and a
 * `host` node the caller renders ONCE at its root.
 *
 * Contract with `runActivationAction`: `openDialog` returns `true` when this host owns
 * the dialog kind and `false` when it does not — the dispatcher then falls back to the
 * item's `fallbackRoute` exactly as it does when no host is mounted at all.
 *
 * Router-free (DC-9): `projectId` is a prop, no react-router import anywhere here.
 */
import React, { useCallback, useMemo, useState } from 'react';
import {
  AddIntegrationDialog,
  PLATFORM_INFO,
  useAnalyticsSettings,
  type AnalyticsPlatform,
  type PlatformConfig,
} from '@modules/analytics-settings';
import type { ActivationAction } from '@modules/shared-types';
import { DomainConnectDrawer } from './DomainConnectDrawer';

type ActivationDialog = NonNullable<ActivationAction['dialog']>;
/** The dialog kinds this host actually mounts. Anything else falls back to a route. */
export const HOSTED_ACTIVATION_DIALOGS: readonly ActivationDialog[] = ['domainDrawer', 'addIntegration'];
export type HostedActivationDialog = 'domainDrawer' | 'addIntegration';

const isHosted = (dialog: ActivationDialog): dialog is HostedActivationDialog =>
  (HOSTED_ACTIVATION_DIALOGS as readonly string[]).includes(dialog);

/** `dialogProps.initialPlatform` is server-supplied copy-free data — accept it only when it names a real platform. */
function resolveInitialPlatform(value: string | undefined): AnalyticsPlatform | undefined {
  if (!value) return undefined;
  return Object.prototype.hasOwnProperty.call(PLATFORM_INFO, value) ? (value as AnalyticsPlatform) : undefined;
}

interface AddIntegrationHostProps {
  projectId: string;
  initialPlatform?: AnalyticsPlatform;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Mounted ONLY while the dialog is open: `useAnalyticsSettings` opens a Firestore
 * subscription, and the sidebar host lives on every page of the app — it must not hold
 * a listener for a dialog nobody opened.
 */
const AddIntegrationHost: React.FC<AddIntegrationHostProps> = ({ projectId, initialPlatform, onClose, onSaved }) => {
  const { settings, addIntegration } = useAnalyticsSettings(projectId);

  // Same wiring as AnalyticsIntegrationContent.handleAddIntegration (the canonical host):
  // the dialog closes itself after `onAdd` resolves, so this only has to persist + refresh.
  const handleAdd = useCallback(
    async (platform: AnalyticsPlatform, name: string, config: PlatformConfig): Promise<void> => {
      await addIntegration(platform, name, config);
      onClose();
      onSaved();
    },
    [addIntegration, onClose, onSaved],
  );

  return (
    <AddIntegrationDialog
      open
      onClose={onClose}
      onAdd={handleAdd}
      existingIntegrations={settings?.integrations ?? []}
      projectId={projectId}
      initialPlatform={initialPlatform}
    />
  );
};

export interface ActivationDialogHostProps {
  projectId: string;
  /** The open dialog, or null when none is open. */
  dialog: HostedActivationDialog | null;
  dialogProps?: Record<string, string>;
  onClose: () => void;
  /** Called after a hosted dialog persisted something — the caller refetches the projection. */
  onSaved: () => void;
}

export const ActivationDialogHost: React.FC<ActivationDialogHostProps> = ({ projectId, dialog, dialogProps, onClose, onSaved }) => (
  <>
    <DomainConnectDrawer projectId={projectId} open={dialog === 'domainDrawer'} onClose={onClose} />
    {dialog === 'addIntegration' && (
      <AddIntegrationHost
        projectId={projectId}
        initialPlatform={resolveInitialPlatform(dialogProps?.initialPlatform)}
        onClose={onClose}
        onSaved={onSaved}
      />
    )}
  </>
);

export interface UseActivationDialogHostOptions {
  /** Invoked after a hosted dialog saved — wire it to the activation `refetch`. */
  onSaved: () => void;
}

export interface UseActivationDialogHostResult {
  /** Hand this to `ActivationChecklist openDialog=` on every surface of this host. */
  openDialog: (dialog: ActivationDialog, props?: Record<string, string>) => boolean;
  /** Render ONCE at the host's root (outside any surface that may be mounted twice). */
  host: React.ReactNode;
}

export function useActivationDialogHost(
  projectId: string | undefined,
  { onSaved }: UseActivationDialogHostOptions,
): UseActivationDialogHostResult {
  const [open, setOpen] = useState<{ dialog: HostedActivationDialog; props?: Record<string, string> } | null>(null);

  const openDialog = useCallback(
    (dialog: ActivationDialog, props?: Record<string, string>): boolean => {
      if (!projectId || !isHosted(dialog)) return false;
      setOpen({ dialog, props });
      return true;
    },
    [projectId],
  );

  const close = useCallback(() => setOpen(null), []);

  const host = useMemo(
    () =>
      projectId ? (
        <ActivationDialogHost
          projectId={projectId}
          dialog={open?.dialog ?? null}
          dialogProps={open?.props}
          onClose={close}
          onSaved={onSaved}
        />
      ) : null,
    [projectId, open, close, onSaved],
  );

  return { openDialog, host };
}

export default ActivationDialogHost;
